import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, DollarSign, TrendingUp, Shield, ArrowRight, Plus } from 'lucide-react';
import { superAdminAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [metricsRes, tenantsRes] = await Promise.all([
          superAdminAPI.getMetrics(),
          superAdminAPI.getTenants({ limit: 10 })
        ]);
        setMetrics(metricsRes.data.data);
        setTenants(tenantsRes.data.data || []);
      } catch {
        toast.error('Failed to load metrics');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  const planColors = { basic: '#6B7280', pro: '#3B82F6', enterprise: '#8B5CF6' };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Super Admin Console</h1>
          <p className="page-subtitle">Platform-wide metrics and tenant management</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/super-admin/tenants')}>
          <Plus size={16} /> New Tenant
        </button>
      </div>

      {/* Metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 28 }}>
        {[
          { label: 'Total Tenants', value: metrics?.totalTenants || 0, icon: Building2, color: '#3B82F6', bg: '#EFF6FF' },
          { label: 'Active Subscriptions', value: metrics?.activeTenants || 0, icon: Shield, color: '#10B981', bg: '#ECFDF5' },
          { label: 'Trial Accounts', value: metrics?.trialTenants || 0, icon: Users, color: '#F59E0B', bg: '#FFFBEB' },
          { label: 'MRR', value: `$${(metrics?.mrr || 0).toLocaleString()}`, icon: DollarSign, color: '#8B5CF6', bg: '#F5F3FF' },
          { label: 'ARR', value: `$${(metrics?.arr || 0).toLocaleString()}`, icon: TrendingUp, color: '#10B981', bg: '#ECFDF5' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div className="stat-card" key={label}>
            <div className="stat-icon" style={{ background: bg }}><Icon size={22} color={color} /></div>
            <div><div className="stat-value">{value}</div><div className="stat-label">{label}</div></div>
          </div>
        ))}
      </div>

      {/* Plan breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Plan Distribution</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(metrics?.planBreakdown || []).map(p => (
              <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 80, fontSize: 13, fontWeight: 500, textTransform: 'capitalize', color: planColors[p._id] || '#374151' }}>
                  {p._id}
                </span>
                <div style={{ flex: 1, background: '#F3F4F6', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                  <div style={{
                    width: `${metrics.totalTenants > 0 ? (p.count / metrics.totalTenants) * 100 : 0}%`,
                    background: planColors[p._id] || '#3B82F6', height: '100%', borderRadius: 4,
                    transition: 'width 0.5s ease'
                  }} />
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', width: 30, textAlign: 'right' }}>{p.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Recent Tenants</span>
            <button className="btn btn-sm btn-ghost" onClick={() => navigate('/super-admin/tenants')}>
              View all <ArrowRight size={14} />
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {tenants.slice(0, 6).map(t => (
              <div key={t._id}
                onClick={() => navigate(`/super-admin/tenants/${t._id}`)}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>{t.email}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span className="badge" style={{ color: planColors[t.subscription?.plan], background: `${planColors[t.subscription?.plan]}18` }}>
                    {t.subscription?.plan}
                  </span>
                  <span className={`badge ${t.subscription?.status === 'active' ? 'badge-green' : t.subscription?.status === 'trial' ? 'badge-yellow' : 'badge-red'}`}>
                    {t.subscription?.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
